<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/AppStore'
import { useCartStore } from '@/stores/CartStore'
import { computed, ref, defineAsyncComponent, Teleport } from 'vue'
import NavSearch from './NavSearch.vue'
import NavCategories from './menus/NavCategories.vue'

const { t } = useI18n()
const cartStore = useCartStore()
const appStore = useAppStore()
const { isSignedIn, getCurrentCustomer, isFreeTransportNotice } = storeToRefs(appStore)
const { getTotalItemsInCart, hasFreeTransport } = storeToRefs(cartStore)

const BaseOdometer = defineAsyncComponent(() => import('@/components/general/BaseOdometer.vue'))
const FreeTransportAlert = defineAsyncComponent(() => import('./FreeTransportAlert.vue'))

const freeShippingProgress = computed(() => {
  if (isSignedIn.value && getCurrentCustomer.value && getCurrentCustomer.value.inPlusClub) {
    return 100;
  } else if (isNaN(cartStore.getCart?.priceUntilFreeTransport)) {
    return 0;
  } else if (cartStore.getCart?.priceUntilFreeTransport !== 0) {
    return (
      (Number.parseInt(t('free_shipping')) - cartStore.getCart?.priceUntilFreeTransport) /
      (Number.parseInt(t('free_shipping')) / 100)
    )
  } else return 100;
})
const freeShippingPrice = computed<number>(() => {
  if (cartStore.getTotalItemsInCart > 0) {
    return Number(cartStore.getCart?.priceUntilFreeTransport ?? 0)
  }
  const parsed = Number.parseInt(String(t('free_shipping')), 10)
  return Number.isNaN(parsed) ? 0 : parsed
})

const freeDeliveryMsgShow = ref(true)
const freeShippingMsgHandler = () => {
  freeDeliveryMsgShow.value = false
}
</script>

<template>
  <Teleport to="body">
    <nav class="mobile-nav sticky bottom-0 left-0 w-100 zindex-11 bg-primary-100 xl:hidden block">
      <ul class="list-unstyled m-0 py-2 flex">
        <li class="flex-1">
          <RouterLink
            to="/"
            class="flex flex-col no-decoration items-center"
          >
            <i class="mi-home icon-24" />
            <span class="text-xs mt-1 text-center">{{ t('home') }}</span>
          </RouterLink>
        </li>
        <li class="flex-1">
          <VDropdown
            theme="navItem"
            class="w-100"
            :positioning-disabled="true"
            popper-class="navItem-max"
          >
            <Button
              to="/"
              class="btn p-0 m-0 flex flex-col no-decoration items-center w-100 shadow-none"
            >
              <i class="mi-goods icon-24" />
              <span class="text-xs mt-1 text-center">{{ t('products') }}</span>
            </Button>
            <template #popper>
              <NavCategories
                :active-tab-prop="true"
                class="pb-8"
              />
            </template>
          </VDropdown>
        </li>
        <li class="flex-1">
          <VDropdown
            theme="navItem"
            class="w-100"
            :positioning-disabled="true"
            popper-class="navItem-max"
          >
            <Button
              to="/"
              class="btn p-0 m-0 flex flex-col no-decoration items-center w-100 shadow-none"
            >
              <i class="mi-mix icon-24" />
              <span class="text-xs mt-1 text-center">{{ t('mix') }}</span>
            </Button>
            <template #popper> 
              <NavCategories
                :active-tab-prop="false"
                class="pb-8"
              />
            </template>
          </VDropdown>
        </li>
        <li class="flex-1">
          <VDropdown
            theme="navItem"
            :auto-hide="false"
            placement="top"
            :triggers="[]"
            class="w-100"
            strategy="fixed"
            popper-class="navItem-free-delivery navItem-free-delivery-mobile flex xl:hidden"
            :shown="freeDeliveryMsgShow"
          >
            <RouterLink
              :to="{ name: 'cartsPath' }"
              class="flex flex-col no-decoration items-center"
            >
              <div class="relative">
                <span
                  v-if="getTotalItemsInCart > 0"
                  class="cart-quantity-badge absolute bg-danger rounded-pill text-nowrap text-sm text-white leading-1 mt-n1 mr-n2 right-0 top-0"
                >
                  <BaseOdometer :number="getTotalItemsInCart" />
                </span>
                <i
                  class="icon-24"
                  :class="getTotalItemsInCart > 0 ? 'mi-cart-fill' : 'mi-cart'"
                />
                <div
                  v-if="isFreeTransportNotice"
                  class="progress-bar absolute mt-n1 overflow-hidden"
                >
                  <div
                    class="progress-percent"
                    :style="{ width: `${freeShippingProgress}%` }"
                  />
                </div>
              </div>
              <span class="text-xs mt-1 text-center">{{ t('cart') }}</span>
            </RouterLink>
            <template #popper>
              <FreeTransportAlert
                :has-free-transport="hasFreeTransport"
                :is-plus-club="isSignedIn && (getCurrentCustomer?.inPlusClub || false)"
                :free-shipping-price="freeShippingPrice"
                @close-transport-alert="freeShippingMsgHandler"
              />
            </template>
          </VDropdown>
        </li>
        <li class="flex-1">
          <NavSearch
            type="mobile"
            :is-touch-screen="true"
          />
        </li>
      </ul>
    </nav>
  </Teleport>
</template>

<style lang="scss" scoped>
.mobile-nav {
    box-shadow: 0 -10px 20px rgba(33, 14, 1, 0.1);
    padding-bottom: env(safe-area-inset-bottom);
    z-index: 1100;
}
.flex-1 {
  flex: 1;
}
.progress-bar {
  border-radius: 50rem;
  background-color: #4696ef;
  width: 30px;
  height: 8px;
  left: 50%;
  transform: translateX(-50%);

  .progress-percent {
    background-color: #22548c;
    height: 100%;
    max-width: 100%;
    transition: .5s width;
  }
}
.cart-quantity-badge {
  width: 20px;
  height: 20px;
}
.icon-mixit-plus {
  width: 8px;
  height: 8px;
  left: 20px;
  top: -3px;
}
</style>

<i18n>
  {
    "cs": {
    "cart": "Košík",
    "free_shipping": "1499",
    "home": "Domů",
    "mix": "Mixovat",
    "products": "Nabídka",
    "search": "Hledat"
},
    "sk": {
    "cart": "Košík",
    "free_shipping": "59",
    "home": "Domov",
    "mix": "Mixovať",
    "products": "Ponuka",
    "search": "Hľadať"
},
    "pl": {
    "cart": "Koszyk",
    "free_shipping": "199",
    "home": "Strona główna",
    "mix": "Mieszaj",
    "products": "Oferta",
    "search": "Szukaj"
},
    "hu": {
    "cart": "Kosár",
    "free_shipping": "18990",
    "home": "Kezdőlap",
    "mix": "Mixelni",
    "products": "Ajánlat",
    "search": "Keresés"
},
    "de": {
    "cart": "Warenkorb",
    "free_shipping": "49",
    "home": "Nach Hause",
    "mix": "Mixen",
    "products": "Angebot",
    "search": "Suchen"
},
    "at": {
    "cart": "Warenkorb",
    "free_shipping": "49",
    "home": "Nach Hause",
    "mix": "Mixen",
    "products": "Angebot",
    "search": "Suchen"
},
    "ro": {
    "cart": "Coș",
    "free_shipping": "199",
    "home": "Pagina principală",
    "mix": "Mixează",
    "products": "Ofertă",
    "search": "Căutare"
},
    "fr": {
    "cart": "Panier",
    "free_shipping": "1499",
    "home": "Accueil",
    "mix": "Créer",
    "products": "Offre",
    "search": "Rechercher"
}
  }
</i18n>